import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { matchmaking, OPPONENT_REQUEST_STATUSES } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock,
  Filter,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
  Swords,
  Target,
  Trophy,
  UserCheck,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';


const LEVELS = ['beginner', 'amateur', 'intermediate', 'advanced', 'professional', 'elite'];
const TIME_SLOTS = ['morning', 'afternoon', 'evening', 'weekend'];

const STATUS_META = {
  [OPPONENT_REQUEST_STATUSES.PENDING]: {
    labelKey: 'matchmaking.status.pending',
    className: 'bg-amber-100 text-amber-800 border-amber-200',
    color: 'text-amber-700',
  },
  [OPPONENT_REQUEST_STATUSES.ACCEPTED]: {
    labelKey: 'matchmaking.status.accepted',
    className: 'bg-lime-100 text-lime-800 border-lime-200',
    color: 'text-lime-700',
  },
  [OPPONENT_REQUEST_STATUSES.DECLINED]: {
    labelKey: 'matchmaking.status.declined',
    className: 'bg-rose-100 text-rose-800 border-rose-200',
    color: 'text-rose-700',
  },
  [OPPONENT_REQUEST_STATUSES.CANCELED]: {
    labelKey: 'matchmaking.status.canceled',
    className: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    color: 'text-zinc-700',
  },
  expired: {
    labelKey: 'matchmaking.status.expired',
    className: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    color: 'text-zinc-700',
  },
};

const SURFACE_META = {
  hard: 'bg-blue-100 text-blue-800 border-blue-200',
  clay: 'bg-orange-100 text-orange-800 border-orange-200',
  grass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  carpet: 'bg-purple-100 text-purple-800 border-purple-200',
  indoor: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center shadow-sm">
    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
      <Icon size={30} />
    </div>
    <h2 className="mt-5 text-2xl font-semibold tracking-tight text-zinc-950">{title}</h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">{body}</p>
  </div>
);

export default function MatchmakingPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const levelLabel = (level) => t(`matchmaking.levels.${level}`);
  const timeLabel = (slot) => t(`matchmaking.${slot}`);
  const statusLabel = (status) => t(STATUS_META[status]?.labelKey || `matchmaking.status.${status}`);
  const surfaceLabel = (surface) => t(`matchmaking.surfaces.${surface}`, { defaultValue: surface });
  const [filters, setFilters] = useState({ level: '', city: '', preferred_time: '' });
  const [candidates, setCandidates] = useState([]);
  const [requests, setRequests] = useState({ sent: [], received: [] });
  const [searching, setSearching] = useState(false);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestsError, setRequestsError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [activeTab, setActiveTab] = useState('incoming');

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError('');
    try {
      const res = await matchmaking.getRequests();
      setRequests({
        sent: Array.isArray(res.data?.sent) ? res.data.sent : [],
        received: Array.isArray(res.data?.received) ? res.data.received : [],
      });
    } catch {
      setRequestsError(t('matchmaking.searchError'));
    } finally {
      setRequestsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleSearch = async () => {
    setSearching(true);
    setSearchError('');
    try {
      const body = {};
      if (filters.level) body.level = filters.level;
      if (filters.city.trim()) body.city = filters.city.trim();
      if (filters.preferred_time) body.preferred_time = filters.preferred_time;
      const res = await matchmaking.find(body);
      setCandidates(Array.isArray(res.data) ? res.data : []);
      setActiveTab('suggested');
      if (res.data.length === 0) toast.info(t('common.noData'));
    } catch (err) {
      const msg = err?.response?.data?.detail || t('matchmaking.searchError');
      setSearchError(msg);
      toast.error(msg);
    } finally {
      setSearching(false);
    }
  };

  const handleSendRequest = async (candidate) => {
    try {
      await matchmaking.sendRequest({
        to_athlete_id: candidate.user_id,
        proposed_level: filters.level || candidate.level,
        proposed_city: filters.city.trim() || candidate.city,
        proposed_time: filters.preferred_time || undefined,
      });
      toast.success(t('matchmaking.requestSentTo', { name: candidate.name }));
      loadRequests();
      setActiveTab('sent');
    } catch (err) {
      const msg = err?.response?.data?.detail || t('opponent.requestError');
      toast.error(msg);
    }
  };

  const handleAccept = async (requestId) => {
    try {
      await matchmaking.accept(requestId);
      toast.success(t('matchmaking.matchAccepted'));
      loadRequests();
    } catch (err) {
      const msg = err?.response?.data?.detail || t('matchmaking.searchError');
      toast.error(msg);
    }
  };

  const pendingReceived = useMemo(
    () => requests.received.filter((request) => request.status === OPPONENT_REQUEST_STATUSES.PENDING),
    [requests.received]
  );

  const acceptedRequests = useMemo(
    () => [...requests.received, ...requests.sent].filter((request) => request.status === OPPONENT_REQUEST_STATUSES.ACCEPTED),
    [requests]
  );

  const topCandidate = candidates[0];
  const activeFilters = [filters.level, filters.city, filters.preferred_time].filter(Boolean).length;

  const tabs = [
    { key: 'incoming', label: t('matchmaking.tabs.incoming'), count: pendingReceived.length },
    { key: 'suggested', label: t('matchmaking.tabs.suggested'), count: candidates.length },
    { key: 'sent', label: t('matchmaking.tabs.sent'), count: requests.sent.length },
    { key: 'create', label: t('matchmaking.tabs.create'), count: activeFilters },
  ];

  const summaryCards = [
    {
      label: t('matchmaking.actionNeeded'),
      value: pendingReceived.length,
      detail: t('matchmaking.incomingPendingRequests'),
      Icon: UserCheck,
      color: pendingReceived.length ? 'text-lime-700' : 'text-zinc-600',
    },
    {
      label: t('matchmaking.tabs.suggested'),
      value: candidates.length,
      detail: topCandidate ? t('matchmaking.topCandidateDetail', { name: topCandidate.name }) : t('matchmaking.runFinderToPopulate'),
      Icon: Sparkles,
      color: 'text-blue-700',
    },
    {
      label: t('matchmaking.status.accepted'),
      value: acceptedRequests.length,
      detail: t('matchmaking.confirmedMatchRequests'),
      Icon: CheckCircle2,
      color: acceptedRequests.length ? 'text-lime-700' : 'text-zinc-600',
    },
    {
      label: t('matchmaking.filters'),
      value: activeFilters,
      detail: t('matchmaking.activeRequestFields'),
      Icon: Filter,
      color: 'text-zinc-700',
    },
  ];

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="matchmaking-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-6 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('matchmaking.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('matchmaking.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('matchmaking.heroBody')}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300">{t('matchmaking.readyToRespond')}</span>
                <Swords className="text-lime-300" size={22} />
              </div>
              <div className="text-4xl font-semibold tracking-tight text-white">{pendingReceived.length}</div>
              <p className="mt-2 text-sm text-zinc-300">{t('matchmaking.readyBody')}</p>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, detail, Icon, color }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={20} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 truncate text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-3 shadow-sm">
              <div className="grid gap-2 sm:grid-cols-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`min-h-11 rounded-full px-4 text-sm font-semibold transition ${
                      activeTab === tab.key
                        ? 'bg-zinc-950 text-white'
                        : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-950'
                    }`}
                  >
                    {tab.label}
                    {tab.count ? <span className="ml-2 opacity-75">({tab.count})</span> : null}
                  </button>
                ))}
              </div>
            </div>

            {(activeTab === 'create' || activeTab === 'suggested') && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="mb-5 flex items-center gap-2 text-sm font-semibold text-zinc-700">
                  <Search size={18} />
                  {t('matchmaking.filters')}
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('matchmaking.level')}</label>
                    <select
                      value={filters.level}
                      onChange={(e) => setFilters((current) => ({ ...current, level: e.target.value }))}
                      className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    >
                      <option value="">{t('matchmaking.anyLevel')}</option>
                      {LEVELS.map((level) => (
                        <option key={level} value={level}>{levelLabel(level)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('matchmaking.city')}</label>
                    <input
                      type="text"
                      value={filters.city}
                      onChange={(e) => setFilters((current) => ({ ...current, city: e.target.value }))}
                      className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('matchmaking.proposedTime')}</label>
                    <select
                      value={filters.preferred_time}
                      onChange={(e) => setFilters((current) => ({ ...current, preferred_time: e.target.value }))}
                      className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    >
                      <option value="">{t('matchmaking.anyTime')}</option>
                      {TIME_SLOTS.map((slot) => (
                        <option key={slot} value={slot}>{timeLabel(slot)}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={searching}
                  className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
                >
                  <Search size={17} />
                  {searching ? t('common.loading') : t('common.search')}
                </button>
              </div>
            )}

            {activeTab === 'incoming' && (
              <RequestSection
                title={t('matchmaking.incomingRequests')}
                body={t('matchmaking.incomingRequestsBody')}
                requests={requests.received}
                loading={requestsLoading}
                error={requestsError}
                emptyTitle={t('matchmaking.noIncomingRequests')}
                emptyBody={t('matchmaking.noIncomingRequestsBody')}
                direction="incoming"
                onAccept={handleAccept}
                statusLabel={statusLabel}
                levelLabel={levelLabel}
                timeLabel={timeLabel}
                surfaceLabel={surfaceLabel}
                t={t}
              />
            )}

            {activeTab === 'suggested' && (
              <div className="space-y-4">
                {searching && <CandidateSkeleton />}
                {!searching && searchError && (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
                    <AlertTriangle className="mx-auto text-rose-600" size={36} />
                    <h2 className="mt-4 text-xl font-semibold tracking-tight text-rose-950">{t('matchmaking.suggestionsLoadError')}</h2>
                    <p className="mt-2 text-sm text-rose-700">{searchError}</p>
                  </div>
                )}
                {!searching && !searchError && candidates.length === 0 && (
                  <EmptyState
                    icon={Sparkles}
                    title={t('matchmaking.noSuggestionsTitle')}
                    body={t('matchmaking.noSuggestionsBody')}
                  />
                )}
                {!searching && !searchError && candidates.length > 0 && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {candidates.map((candidate) => (
                      <CandidateCard
                        key={candidate.user_id}
                        candidate={candidate}
                        onRequest={() => handleSendRequest(candidate)}
                        challengeLabel={t('matchmaking.sendRequest')}
                        levelLabel={levelLabel}
                        surfaceLabel={surfaceLabel}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sent' && (
              <RequestSection
                title={t('matchmaking.sentRequests')}
                body={t('matchmaking.sentRequestsBody')}
                requests={requests.sent}
                loading={requestsLoading}
                error={requestsError}
                emptyTitle={t('matchmaking.noSentRequests')}
                emptyBody={t('matchmaking.noSentRequestsBody')}
                direction="sent"
                statusLabel={statusLabel}
                levelLabel={levelLabel}
                timeLabel={timeLabel}
                surfaceLabel={surfaceLabel}
                t={t}
              />
            )}

            {activeTab === 'create' && (
              <EmptyState
                icon={Target}
                title={t('matchmaking.createFromSuggestion')}
                body={t('matchmaking.createFromSuggestionBody')}
              />
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('matchmaking.bestSuggestion')}</h2>
                  <p className="text-sm text-zinc-500">{t('matchmaking.bestSuggestionBody')}</p>
                </div>
                <Trophy className="text-lime-700" size={22} />
              </div>
              {topCandidate ? (
                <div className="rounded-xl border border-lime-100 bg-lime-50 p-4">
                  <p className="text-xl font-semibold tracking-tight text-zinc-950">{topCandidate.name}</p>
                  <p className="mt-1 text-sm text-lime-800">{[topCandidate.city, topCandidate.level && levelLabel(topCandidate.level)].filter(Boolean).join(' - ')}</p>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <MiniStat label={t('matchmaking.scoreLabel')} value={topCandidate.score ?? 0} />
                    <MiniStat label={t('matchmaking.h2hLabel')} value={`${topCandidate.h2h_wins || 0}-${topCandidate.h2h_losses || 0}`} />
                  </div>
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('matchmaking.noTopCandidateBody')}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('matchmaking.requestStatus')}</h2>
                  <p className="text-sm text-zinc-500">{t('matchmaking.requestStatusBody')}</p>
                </div>
                <ShieldCheck className="text-zinc-700" size={22} />
              </div>
              <div className="space-y-3">
                {Object.values(OPPONENT_REQUEST_STATUSES).map((status) => {
                  const count = [...requests.sent, ...requests.received].filter((request) => request.status === status).length;
                  return (
                    <div key={status} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                      <StatusBadge status={status} statusLabel={statusLabel} />
                      <span className="text-sm font-semibold text-zinc-950">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('matchmaking.proposalFields')}</h2>
                  <p className="text-sm text-zinc-500">{t('matchmaking.proposalFieldsBody')}</p>
                </div>
                <CalendarDays className="text-blue-700" size={22} />
              </div>
              <div className="space-y-3 text-sm">
                <ProposalRow label={t('matchmaking.level')} value={filters.level ? levelLabel(filters.level) : t('matchmaking.optional')} />
                <ProposalRow label={t('matchmaking.city')} value={filters.city.trim() || t('matchmaking.optional')} />
                <ProposalRow label={t('matchmaking.proposedTime')} value={filters.preferred_time ? timeLabel(filters.preferred_time) : t('matchmaking.optional')} />
                <p className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 leading-6 text-zinc-500">
                  {t('matchmaking.unsupportedFieldsNote')}
                </p>
              </div>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
}

function CandidateSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {[0, 1, 2, 3].map((item) => (
        <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
          <div className="h-1.5 rounded bg-zinc-100" />
          <div className="mt-5 h-5 w-40 animate-pulse rounded bg-zinc-200" />
          <div className="mt-4 h-20 animate-pulse rounded-xl bg-zinc-100" />
        </div>
      ))}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-lime-100 bg-white p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tracking-tight text-zinc-950">{value}</p>
    </div>
  );
}

function ProposalRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
      <span className="font-medium text-zinc-600">{label}</span>
      <span className="font-semibold text-zinc-950">{value}</span>
    </div>
  );
}

function StatusBadge({ status, statusLabel }) {
  const meta = STATUS_META[status] || STATUS_META[OPPONENT_REQUEST_STATUSES.PENDING];
  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${meta.className}`}>
      {statusLabel(status)}
    </span>
  );
}

function DirectionBadge({ direction, t }) {
  const incoming = direction === 'incoming';
  return (
    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${
      incoming ? 'border-lime-200 bg-lime-50 text-lime-800' : 'border-blue-200 bg-blue-50 text-blue-800'
    }`}>
      {incoming ? t('matchmaking.tabs.incoming') : t('matchmaking.tabs.sent')}
    </span>
  );
}

function CandidateCard({ candidate, onRequest, challengeLabel, levelLabel, surfaceLabel, t }) {
  const score = Number(candidate.score || 0);
  const scorePercent = Math.min(Math.round((score / 8.5) * 100), 100);

  return (
    <article className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-xl hover:shadow-zinc-200/70">
      <div className="h-1.5 bg-gradient-to-r from-lime-300 to-emerald-500" />
      <div className="p-5">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="truncate text-lg font-semibold tracking-tight text-zinc-950">{candidate.name}</h3>
            <p className="mt-2 flex items-center gap-2 text-sm text-zinc-500">
              <MapPin size={15} />
              {candidate.city || t('matchmaking.cityNotSet')}
            </p>
          </div>
          {candidate.level && (
            <span className="shrink-0 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-700">
              {levelLabel(candidate.level)}
            </span>
          )}
        </div>

        <div className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
          <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-zinc-500">
            <span>{t('matchmaking.matchScore')}</span>
            <span>{score}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-zinc-200">
            <div className="h-2 rounded-full bg-lime-500" style={{ width: `${scorePercent}%` }} />
          </div>
          <p className="mt-2 text-xs text-zinc-500">{t('matchmaking.scorePolicy', { version: candidate.score_policy_version || 'v1.0' })}</p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {candidate.preferred_surfaces?.length ? (
            candidate.preferred_surfaces.slice(0, 3).map((surface) => (
              <span key={surface} className={`rounded-full border px-3 py-1 text-xs font-semibold ${SURFACE_META[surface] || SURFACE_META.indoor}`}>
                {surfaceLabel(surface)}
              </span>
            ))
          ) : (
            <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-600">
              {t('matchmaking.surfaceNotSet')}
            </span>
          )}
          <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-semibold text-zinc-600">
            H2H {candidate.h2h_wins || 0}-{candidate.h2h_losses || 0}
          </span>
          {candidate.training_frequency_per_week && (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
              {t('matchmaking.perWeek', { count: candidate.training_frequency_per_week })}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={onRequest}
          className="mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-950 px-4 text-sm font-semibold text-white transition hover:bg-zinc-800"
        >
          <Swords size={17} />
          {challengeLabel}
        </button>
      </div>
    </article>
  );
}

function RequestSection({
  title,
  body,
  requests,
  loading,
  error,
  emptyTitle,
  emptyBody,
  direction,
  onAccept,
  statusLabel,
  levelLabel,
  timeLabel,
  surfaceLabel,
  t,
}) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="h-5 w-40 animate-pulse rounded bg-zinc-200" />
        <div className="mt-5 space-y-3">
          {[0, 1, 2].map((item) => (
            <div key={item} className="h-24 animate-pulse rounded-xl bg-zinc-100" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
        <AlertTriangle className="mx-auto text-rose-600" size={36} />
        <h2 className="mt-4 text-xl font-semibold tracking-tight text-rose-950">{t('matchmaking.requestsLoadError')}</h2>
        <p className="mt-2 text-sm text-rose-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="mb-5">
        <h2 className="text-xl font-semibold tracking-tight text-zinc-950">{title}</h2>
        <p className="mt-1 text-sm text-zinc-500">{body}</p>
      </div>
      {requests.length === 0 ? (
        <EmptyState icon={Clock} title={emptyTitle} body={emptyBody} />
      ) : (
        <div className="space-y-3">
          {requests.map((request) => (
            <RequestCard
              key={request.id}
              req={request}
              direction={direction}
              onAccept={
                direction === 'incoming' && request.status === OPPONENT_REQUEST_STATUSES.PENDING
                  ? () => onAccept(request.id)
                  : null
              }
              statusLabel={statusLabel}
              levelLabel={levelLabel}
              timeLabel={timeLabel}
              surfaceLabel={surfaceLabel}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ req, onAccept, direction, statusLabel, levelLabel, timeLabel, surfaceLabel, t }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap gap-2">
            <StatusBadge status={req.status} statusLabel={statusLabel} />
            <DirectionBadge direction={direction} t={t} />
          </div>
          <p className="text-sm text-zinc-500">
            {direction === 'incoming'
              ? t('matchmaking.fromValue', { id: req.from_athlete_id })
              : t('matchmaking.toValue', { id: req.to_athlete_id })}
          </p>
          <div className="flex flex-wrap gap-2">
            {req.proposed_level && (
              <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-700">
                {levelLabel(req.proposed_level)}
              </span>
            )}
            {req.proposed_city && (
              <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800">
                {req.proposed_city}
              </span>
            )}
            {req.proposed_time && (
              <span className="rounded-full border border-lime-200 bg-lime-50 px-3 py-1 text-xs font-semibold text-lime-800">
                {timeLabel(req.proposed_time)}
              </span>
            )}
            {req.proposed_surface && (
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${SURFACE_META[req.proposed_surface] || SURFACE_META.indoor}`}>
                {surfaceLabel(req.proposed_surface)}
              </span>
            )}
          </div>
        </div>
        {onAccept && (
          <button
            type="button"
            onClick={onAccept}
            className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-full bg-lime-400 px-5 text-sm font-semibold text-zinc-950 transition hover:bg-lime-300"
          >
            <CheckCircle2 size={18} />
            {t('matchmaking.acceptRequest')}
          </button>
        )}
      </div>
    </div>
  );
}
